import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, PowerOff, Power } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY_FORM = { email: "", name: "", password: "", role: "devotional_admin" };

export default function AdminUsersPage() {
  const { admin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isSuper = admin?.role === "super_admin";

  const reload = () =>
    api
      .get("/admin/users", { params: { requester_id: admin?.id } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    if (isSuper) reload();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.id]);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form, { params: { creator_id: admin.id } });
      toast.success(`Admin "${form.name}" created`);
      setOpen(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/users/${a.id}/toggle-active`, null, { params: { requester_id: admin.id } });
      toast.success(`${a.is_active ? "Deactivated" : "Activated"} ${a.name}`);
      reload();
    } catch (err) {
      toast.error(err.userMessage);
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`, { params: { requester_id: admin.id } });
      toast.success(`Removed ${deleteTarget.name}`);
      reload();
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (!isSuper) {
    return (
      <>
        <PageHeader title="Admin Users" description="Manage who has access to the admin console." />
        <EmptyState
          icon={ShieldCheck}
          title="Super-admin access required"
          description="Only super admins can view and manage admin accounts."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Admin Users"
        description="Manage who has access to the admin console. Only super admins can add or remove."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-admin-button">
                <Plus className="size-4 mr-1.5" /> New admin
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={onCreate}>
                <DialogHeader>
                  <DialogTitle>Invite admin</DialogTitle>
                  <DialogDescription>
                    They'll be able to sign in with the email + password you set here.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="a-name">Name</Label>
                    <Input
                      id="a-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="admin-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="a-email">Email</Label>
                    <Input
                      id="a-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="admin-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="a-pass">Temporary password</Label>
                    <Input
                      id="a-pass"
                      type="password"
                      minLength={6}
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      data-testid="admin-password-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="admin-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="devotional_admin">Devotional admin</SelectItem>
                        <SelectItem value="super_admin">Super admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="admin-submit-button">
                    {saving ? "Saving…" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <Card className="p-4">
          <TableSkeleton rows={4} cols={4} />
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No admins yet"
          description="Invite an admin to get started."
        />
      ) : (
        <Card className="border-border/70 shadow-sm overflow-hidden">
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Admin</th>
                  <th className="text-left font-medium px-4 py-3">Role</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((a) => (
                  <tr key={a.id} data-testid={`admin-row-${a.id}`} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={a.role === "super_admin" ? "default" : "secondary"} className="capitalize">
                        {a.role.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-flex items-center gap-1.5 text-xs " +
                          (a.is_active ? "text-success" : "text-muted-foreground")
                        }
                      >
                        <span className={"size-2 rounded-full " + (a.is_active ? "bg-success" : "bg-muted-foreground")} />
                        {a.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      {a.id !== admin.id && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => toggleActive(a)} data-testid={`admin-toggle-${a.id}`}>
                            {a.is_active ? <PowerOff className="size-3.5 mr-1" /> : <Power className="size-3.5 mr-1" />}
                            {a.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteTarget(a)}
                            data-testid={`admin-delete-${a.id}`}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </>
                      )}
                      {a.id === admin.id && <span className="text-xs text-muted-foreground">You</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <ul className="md:hidden divide-y divide-border">
            {items.map((a) => (
              <li key={a.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant={a.role === "super_admin" ? "default" : "secondary"} className="capitalize text-[10px] py-0">
                        {a.role.replace("_", " ")}
                      </Badge>
                      <span className={"text-[11px] " + (a.is_active ? "text-success" : "text-muted-foreground")}>
                        {a.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  {a.id !== admin.id && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(a)}>
                        {a.is_active ? "Off" : "On"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => setDeleteTarget(a)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the admin account permanently. They'll lose access to the console immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="confirm-delete-admin"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
